import React from 'react';
import {
  Admin,
  Resource,
  defaultTheme,
  CustomRoutes,
} from 'react-admin';
import { Route } from 'react-router-dom';
import { createTheme } from '@mui/material/styles';
import {
  ShoppingBag as ProductIcon,
  Layers as CategoryIcon,
  ShoppingCart as OrderIcon,
  Users as UserIcon,
  MessageSquare as ReviewIcon,
  Ticket as CouponIcon,
  Image as BannerIcon,
  Megaphone as NoticeIcon,
  HelpCircle as InquiryIcon,
  BarChart3 as ReportIcon,
  Clock as DealIcon,
  CircleHelp as FAQIcon,
  Handshake as PartnershipIcon,
  Award as MembershipIcon,
} from 'lucide-react';
import dataProvider from './providers/dataProvider';
import supabaseAuthProvider from './providers/supabaseAuthProvider';
import i18nProvider from './providers/i18nProvider';
import Dashboard from './components/Dashboard';
import CustomLayout from './components/Layout';
import FeaturedProducts from './components/FeaturedProducts';
import DealFeaturedProducts from './components/DealFeaturedProducts';

import LoginPage from './components/LoginPage';

// 리소스 import
import { ProductList, ProductCreate, ProductEdit, ProductShow } from './resources/products';
import { CategoryList, CategoryCreate, CategoryEdit } from './resources/categories';
import { OrderList, OrderShow, OrderEdit } from './resources/orders';
import { OrderSearch } from './resources/order-search';
import { UserList, UserShow, UserEdit, UserCreate } from './resources/users';
import { BannerList, BannerCreate, BannerEdit } from './resources/banners';
import { ReviewList, ReviewShow } from './resources/reviews';
import { CouponList, CouponCreate, CouponEdit, CouponShow } from './resources/coupons';
import { NoticeList, NoticeCreate, NoticeEdit } from './resources/notices';
import { InquiryList, InquiryShow } from './resources/inquiries';
import { DealFeaturedList, DealFeaturedCreate, DealFeaturedEdit, DealFeaturedShow } from './resources/deal-featured';
import { FAQList, FAQCreate, FAQEdit } from './resources/faqs';
import { PartnershipInquiryList, PartnershipInquiryShow, PartnershipInquiryEdit } from './resources/partnership-inquiries';
import { MembershipConfigList, MembershipConfigEdit, MembershipConfigShow } from './resources/membership-configs';
import ShopSettingsPage from './resources/shop-settings';

// 테마 설정 (메인 페이지와 통일)
const theme = createTheme({
  ...defaultTheme,
  palette: {
    primary: {
      main: '#7A68A7', // 메인 페이지와 동일한 브랜드 색상
      light: '#9B8BC4',
      dark: '#6B5B98',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#F97316', // 보조 색상
      light: '#FB923C',
      dark: '#EA580C',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#E73333', // 메인 페이지와 동일
    },
    warning: {
      main: '#FF9800',
    },
    info: {
      main: '#3B82F6',
    },
    success: {
      main: '#4CAF50',
    },
    background: {
      default: '#F9FAFB', // 메인 페이지와 동일한 배경색
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1C1C1E',
      secondary: '#383838',
    },
  },
  typography: {
    fontFamily: [
      'Pretendard',
      '-apple-system',
      'BlinkMacSystemFont',
      'system-ui',
      'Roboto',
      'Helvetica Neue',
      'Segoe UI',
      'Apple SD Gothic Neo',
      'Noto Sans KR',
      'Malgun Gothic',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
        },
      },
    },
  },
});

const App: React.FC = () => {
  return (
    <Admin
      title="CatchU 관리자"
      dataProvider={dataProvider}
      authProvider={supabaseAuthProvider}
      i18nProvider={i18nProvider}
      theme={theme}
      dashboard={Dashboard}
      layout={CustomLayout}
      loginPage={LoginPage}
      requireAuth
    >
      {/* 상품 관리 */}
      <Resource
        name="products"
        options={{ label: '상품 목록' }}
        icon={ProductIcon}
        list={ProductList}
        edit={ProductEdit}
        create={ProductCreate}
        show={ProductShow}
      />

      {/* 캣츄딜 관리 */}
      <Resource
        name="deal-featured"
        options={{ label: '캣츄딜 관리' }}
        icon={DealIcon}
        list={DealFeaturedList}
        edit={DealFeaturedEdit}
        create={DealFeaturedCreate}
        show={DealFeaturedShow}
        recordRepresentation={(record) => record?.Product?.name || '상품'}
      />

      {/* 카테고리 관리 */}
      <Resource
        name="categories"
        options={{ label: '카테고리 관리' }}
        icon={CategoryIcon}
        list={CategoryList}
        edit={CategoryEdit}
        create={CategoryCreate}
      />

      {/* 주문 관리 */}
      <Resource
        name="orders"
        options={{ label: '주문 목록' }}
        icon={OrderIcon}
        list={OrderList}
        edit={OrderEdit}
        show={OrderShow}
      />

      {/* 회원 관리 */}
      <Resource
        name="users"
        options={{ label: '회원 목록' }}
        icon={UserIcon}
        list={UserList}
        edit={UserEdit}
        show={UserShow}
        create={UserCreate}
      />

      {/* 회원등급 관리 */}
      <Resource
        name="membership-configs"
        options={{ label: '회원등급 관리' }}
        icon={MembershipIcon}
        list={MembershipConfigList}
        edit={MembershipConfigEdit}
        show={MembershipConfigShow}
      />

      {/* 리뷰 관리 */}
      <Resource
        name="reviews"
        options={{ label: '리뷰 목록' }}
        icon={ReviewIcon}
        list={ReviewList}
        show={ReviewShow}
      />

      {/* 쿠폰 관리 */}
      <Resource
        name="coupons"
        options={{ label: '쿠폰 목록' }}
        icon={CouponIcon}
        list={CouponList}
        edit={CouponEdit}
        create={CouponCreate}
        show={CouponShow}
      />

      {/* 배너 관리 */}
      <Resource
        name="banners"
        options={{ label: '배너 목록' }}
        icon={BannerIcon}
        list={BannerList}
        edit={BannerEdit}
        create={BannerCreate}
      />

      {/* 공지사항 */}
      <Resource
        name="notices"
        options={{ label: '공지사항 목록' }}
        icon={NoticeIcon}
        list={NoticeList}
        edit={NoticeEdit}
        create={NoticeCreate}
      />

      {/* FAQ 관리 */}
      <Resource
        name="faqs"
        options={{ label: '자주 묻는 질문' }}
        icon={FAQIcon}
        list={FAQList}
        edit={FAQEdit}
        create={FAQCreate}
      />

      {/* 문의 관리 */}
      <Resource
        name="inquiries"
        options={{ label: '문의 목록' }}
        icon={InquiryIcon}
        list={InquiryList}
        show={InquiryShow}
      />

      {/* 입점/제휴 문의 */}
      <Resource
        name="partnership-inquiries"
        options={{ label: '입점/제휴 문의' }}
        icon={PartnershipIcon}
        list={PartnershipInquiryList}
        edit={PartnershipInquiryEdit}
        show={PartnershipInquiryShow}
      />

      {/* 리포트 (가상 리소스) */}
      <Resource
        name="reports"
        options={{ label: '리포트' }}
        icon={ReportIcon}
      // list={ReportList}
      />

      {/* Custom Routes */}
      <CustomRoutes>
        <Route path="/order-search" element={<OrderSearch />} />
        <Route path="/featured-products" element={<FeaturedProducts />} />
        <Route path="/deal-featured-products" element={<DealFeaturedProducts />} />
        <Route path="/shop-settings" element={<ShopSettingsPage />} />
      </CustomRoutes>
    </Admin>
  );
};

export default App;